/**
 * BuildProposalFromOST
 * 
 * Downstream flow for creating proposal packets from Opportunity Solution Tree.
 * 
 * Flow:
 * 1. Select cycle objective(s)
 * 2. Browse OST trees, select opportunities + solutions
 * 3. System auto-attaches linked Items (or allows creating new)
 * 4. System computes readiness snapshot
 * 5. Review and submit
 * 
 * Per canonical requirements:
 * - Proposals allowed pre-Arrange; readiness disclosure required (Doc 03)
 * - No invisible work; artifacts must persist and be reviewable (Doc 01)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TreeDeciduous,
  Target,
  Lightbulb,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Send,
  AlertTriangle,
  X,
  Loader2,
} from 'lucide-react';
import { useOSTStore, type OSTNode } from '../../../stores/prioritization/ostStore';
import { useProposalPacketStore } from '../../../stores/prioritization/proposalPacketStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { ReadinessSnapshot } from '../../../types/prioritization/catchballProposal';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';

// ============================================================================
// Types
// ============================================================================

interface BuildProposalFromOSTProps {
  cycleId: string;
  loopId: string;
  onClose: () => void;
  onSubmitted: (proposalId: string) => void;
}

// ============================================================================
// Step Indicator
// ============================================================================

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          className={`h-2 w-2 rounded-full ${
            i < currentStep ? 'bg-primary-600' :
            i === currentStep ? 'bg-primary-400' : 'bg-[var(--hover-tint)]'
          }`}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Step 1: Select Objectives
// ============================================================================

function ObjectiveSelector({
  selectedObjectives,
  onToggle,
}: {
  selectedObjectives: Set<string>;
  onToggle: (id: string) => void;
}) {
  // For now, we'll use OST outcomes as proxy for objectives
  // In production, this would connect to the strategy/objectives store
  const trees = useOSTStore(state => state.trees);
  const nodes = useOSTStore(state => state.nodes);
  
  const outcomeNodes = useMemo(() => 
    nodes.filter(n => n.nodeType === 'outcome'),
    [nodes]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Select Objectives</h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Choose the strategic objectives this proposal addresses.
        </p>
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {outcomeNodes.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-ter)]">
            <Target className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
            <p>No outcomes found in OST trees.</p>
            <p className="text-xs mt-1">Create an Opportunity Solution Tree with an outcome first.</p>
          </div>
        ) : (
          outcomeNodes.map(outcome => {
            const tree = trees.find(t => t.id === outcome.treeId);
            const isSelected = selectedObjectives.has(outcome.id);
            
            return (
              <div
                key={outcome.id}
                onClick={() => onToggle(outcome.id)}
                className={`p-3 border  cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(outcome.id)}
                    className="mt-1 h-4 w-4 rounded border-[var(--rule)] text-primary-600"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-[var(--text-ter)]" />
                      <span className="font-medium text-[var(--text-pri)]">{outcome.title}</span>
                    </div>
                    {outcome.description && (
                      <p className="text-sm text-[var(--text-ter)] mt-1">{outcome.description}</p>
                    )}
                    {tree && (
                      <p className="text-xs text-[var(--text-ter)] mt-1">
                        From: {tree.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Step 2: Select OST Nodes
// ============================================================================

function OSTNodeSelector({
  selectedObjectives,
  selectedNodes,
  onToggleNode,
}: {
  selectedObjectives: Set<string>;
  selectedNodes: Set<string>;
  onToggleNode: (id: string) => void;
}) {
  const nodes = useOSTStore(state => state.nodes);
  const trees = useOSTStore(state => state.trees);
  const [expandedTrees, setExpandedTrees] = useState<Set<string>>(new Set());

  // Get opportunities and solutions related to selected objectives
  const relevantNodes = useMemo(() => {
    if (selectedObjectives.size === 0) return [];
    
    // Find all opportunities under selected outcomes
    const opportunityIds = new Set<string>();
    const solutionIds = new Set<string>();
    
    nodes.forEach(node => {
      if (node.nodeType === 'opportunity') {
        // Check if parent is a selected objective
        if (selectedObjectives.has(node.parentId || '')) {
          opportunityIds.add(node.id);
        }
      }
    });
    
    // Find solutions under those opportunities
    nodes.forEach(node => {
      if (node.nodeType === 'solution' && opportunityIds.has(node.parentId || '')) {
        solutionIds.add(node.id);
      }
    });
    
    return nodes.filter(n => 
      opportunityIds.has(n.id) || solutionIds.has(n.id)
    );
  }, [nodes, selectedObjectives]);

  // Group by tree
  const nodesByTree = useMemo(() => {
    const map = new Map<string, OSTNode[]>();
    relevantNodes.forEach(n => {
      const existing = map.get(n.treeId) || [];
      existing.push(n);
      map.set(n.treeId, existing);
    });
    return map;
  }, [relevantNodes]);

  const toggleTree = (treeId: string) => {
    setExpandedTrees(prev => {
      const next = new Set(prev);
      if (next.has(treeId)) {
        next.delete(treeId);
      } else {
        next.add(treeId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Select Opportunities & Solutions</h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Choose the opportunities and solutions to include in this proposal.
        </p>
      </div>
      
      <div className="space-y-3 max-h-80 overflow-y-auto">
        {nodesByTree.size === 0 ? (
          <div className="text-center py-8 text-[var(--text-ter)]">
            <Lightbulb className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
            <p>No opportunities found for selected objectives.</p>
          </div>
        ) : (
          Array.from(nodesByTree.entries()).map(([treeId, treeNodes]) => {
            const tree = trees.find(t => t.id === treeId);
            const isExpanded = expandedTrees.has(treeId);
            const opportunities = treeNodes.filter(n => n.nodeType === 'opportunity');
            const solutions = treeNodes.filter(n => n.nodeType === 'solution');
            
            return (
              <div key={treeId} className="border border-[var(--rule)] overflow-hidden">
                <div
                  onClick={() => toggleTree(treeId)}
                  className="flex items-center justify-between px-4 py-3 bg-[var(--surface)] cursor-pointer hover:bg-[var(--hover-tint)]"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
                    )}
                    <TreeDeciduous className="h-4 w-4 text-[var(--green)]" />
                    <span className="font-medium text-[var(--text-pri)]">{tree?.name || 'Unknown Tree'}</span>
                  </div>
                  <span className="text-xs text-[var(--text-ter)]">
                    {opportunities.length} opportunities, {solutions.length} solutions
                  </span>
                </div>
                
                {isExpanded && (
                  <div className="p-3 space-y-2">
                    {opportunities.map(opp => {
                      const oppSolutions = solutions.filter(s => s.parentId === opp.id);
                      const isOppSelected = selectedNodes.has(opp.id);
                      
                      return (
                        <div key={opp.id} className="pl-4 border-l-2 border-[var(--mustard)]">
                          <div
                            onClick={() => onToggleNode(opp.id)}
                            className={`p-2 rounded cursor-pointer ${
                              isOppSelected ? 'bg-[var(--mustard-dim)]' : 'hover:bg-[var(--surface)]'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isOppSelected}
                                onChange={() => onToggleNode(opp.id)}
                                className="h-4 w-4 rounded border-[var(--rule)] text-[var(--mustard)]"
                              />
                              <Lightbulb className="h-4 w-4 text-[var(--mustard)]" />
                              <span className="text-sm font-medium">{opp.title}</span>
                              {opp.isTargeted && (
                                <span className="px-1.5 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs rounded">
                                  Target
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Solutions under this opportunity */}
                          <div className="ml-6 mt-1 space-y-1">
                            {oppSolutions.map(sol => {
                              const isSolSelected = selectedNodes.has(sol.id);
                              
                              return (
                                <div
                                  key={sol.id}
                                  onClick={() => onToggleNode(sol.id)}
                                  className={`p-2 rounded cursor-pointer ${
                                    isSolSelected ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--surface)]'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={isSolSelected}
                                      onChange={() => onToggleNode(sol.id)}
                                      className="h-4 w-4 rounded border-[var(--rule)] text-[var(--text-ter)]"
                                    />
                                    <CheckCircle2 className="h-4 w-4 text-[var(--text-ter)]" />
                                    <span className="text-sm">{sol.title}</span>
                                    {sol.validationStatus === 'validated' && (
                                      <span className="px-1.5 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs rounded">
                                        Validated
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      
      {selectedNodes.size > 0 && (
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-3">
          <p className="text-sm text-[var(--text-sec)]">
            {selectedNodes.size} node{selectedNodes.size !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Step 3: Review & Submit
// ============================================================================

function ReviewAndSubmit({
  selectedObjectives,
  selectedNodes,
  rationale,
  onRationaleChange,
  readinessSnapshot,
  isComputing,
  isSubmitting,
  onSubmit,
}: {
  selectedObjectives: Set<string>;
  selectedNodes: Set<string>;
  rationale: string;
  onRationaleChange: (value: string) => void;
  readinessSnapshot: ReadinessSnapshot | null;
  isComputing: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
}) {
  const nodes = useOSTStore(state => state.nodes);
  
  const selectedNodeDetails = useMemo(() => 
    nodes.filter(n => selectedNodes.has(n.id)),
    [nodes, selectedNodes]
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)]">Review & Submit</h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Review your proposal before submitting for executive review.
        </p>
      </div>

      {/* Summary */}
      <div className="bg-[var(--surface)] p-4 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-[var(--text-sec)]">Objectives</span>
          <span className="font-medium">{selectedObjectives.size}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[var(--text-sec)]">OST Nodes</span>
          <span className="font-medium">{selectedNodes.size}</span>
        </div>
        
        {/* Node list */}
        <div className="border-t border-[var(--rule)] pt-3 mt-3">
          <p className="text-xs text-[var(--text-ter)] mb-2">Selected nodes:</p>
          <div className="space-y-1">
            {selectedNodeDetails.map(node => (
              <div key={node.id} className="flex items-center gap-2 text-sm">
                {node.nodeType === 'opportunity' ? (
                  <Lightbulb className="h-3.5 w-3.5 text-[var(--mustard)]" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                )}
                <span>{node.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Readiness Snapshot */}
      {isComputing ? (
        <div className="flex items-center gap-2 text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Computing readiness...</span>
        </div>
      ) : readinessSnapshot && (
        <div className="border border-[var(--rule)] p-4">
          <h4 className="text-sm font-medium text-[var(--text-pri)] mb-3">Readiness Snapshot</h4>
          
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-[var(--text-pri)]">
                {readinessSnapshot.summary.totalItems}
              </div>
              <div className="text-xs text-[var(--text-ter)]">Total Items</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
                {readinessSnapshot.summary.itemsReady}
              </div>
              <div className="text-xs text-[var(--text-ter)]">Ready</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-[var(--mustard)]">
                {readinessSnapshot.summary.itemsWithGaps}
              </div>
              <div className="text-xs text-[var(--text-ter)]">With Gaps</div>
            </div>
          </div>
          
          {(readinessSnapshot.summary.missingCriteriaLock || 
            readinessSnapshot.summary.missingRanges.length > 0) && (
            <div className="mt-4 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] mt-0.5" />
                <div className="text-sm text-[var(--mustard)]">
                  {readinessSnapshot.summary.missingCriteriaLock && (
                    <p>Criteria not locked</p>
                  )}
                  {readinessSnapshot.summary.missingRanges.length > 0 && (
                    <p>Missing ranges: {readinessSnapshot.summary.missingRanges.join(', ')}</p>
                  )}
                  <p className="mt-1 text-xs">
                    This proposal will be submitted as pre-comparable.
                    Executive may return with blockers.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rationale */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-pri)] mb-2">
          Rationale
        </label>
        <textarea
          value={rationale}
          onChange={e => onRationaleChange(e.target.value)}
          rows={4}
          className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Explain why these opportunities and solutions address the objectives..."
        />
      </div>

      {/* Submit */}
      <button
        onClick={onSubmit}
        disabled={isSubmitting || selectedNodes.size === 0 || !rationale.trim()}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            {CATCHBALL_LABELS.submitProposal}
          </>
        )}
      </button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function BuildProposalFromOST({
  cycleId,
  loopId,
  onClose,
  onSubmitted,
}: BuildProposalFromOSTProps) {
  // State
  const [step, setStep] = useState(0);
  const [selectedObjectives, setSelectedObjectives] = useState<Set<string>>(new Set());
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [rationale, setRationale] = useState('');
  const [readinessSnapshot, setReadinessSnapshot] = useState<ReadinessSnapshot | null>(null);
  const [isComputing, setIsComputing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stores
  const fetchTrees = useOSTStore(state => state.fetchTrees);
  const createProposalPacket = useProposalPacketStore(state => state.createProposalPacket);
  const computeReadinessSnapshot = useProposalPacketStore(state => state.computeReadinessSnapshot);
  const submitProposal = useProposalPacketStore(state => state.submitProposal);
  const userId = useUserStore(state => state.userId);
  const items = useItemsStore(state => state.items);

  // Load trees on mount
  useEffect(() => {
    fetchTrees();
  }, [fetchTrees]);

  // Compute readiness when entering step 3
  useEffect(() => {
    if (step === 2 && selectedNodes.size > 0) {
      setIsComputing(true);
      
      // Get item IDs linked to selected OST nodes
      const linkedItemIds = items
        .filter(c => c.ostNodeId && selectedNodes.has(c.ostNodeId))
        .map(c => c.id);
      
      computeReadinessSnapshot(cycleId, linkedItemIds)
        .then(snapshot => {
          setReadinessSnapshot(snapshot);
          setIsComputing(false);
        })
        .catch(() => setIsComputing(false));
    }
  }, [step, selectedNodes, cycleId, items, computeReadinessSnapshot]);

  // Handlers
  const toggleObjective = useCallback((id: string) => {
    setSelectedObjectives(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleNode = useCallback((id: string) => {
    setSelectedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!userId || selectedNodes.size === 0) return;
    
    setIsSubmitting(true);
    try {
      // Get linked item IDs
      const linkedItemIds = items
        .filter(c => c.ostNodeId && selectedNodes.has(c.ostNodeId))
        .map(c => c.id);
      
      const proposal = await createProposalPacket({
        cycleId,
        loopId,
        objectiveIds: Array.from(selectedObjectives),
        ostNodeIds: Array.from(selectedNodes),
        rationale,
        createdBy: userId,
        itemIds: linkedItemIds,
      });

      if (proposal) {
        await submitProposal(proposal.id, userId);
        onSubmitted(proposal.id);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    userId,
    selectedObjectives,
    selectedNodes,
    rationale,
    cycleId,
    loopId,
    items,
    createProposalPacket,
    submitProposal,
    onSubmitted,
  ]);

  const canProceed = useMemo(() => {
    switch (step) {
      case 0: return selectedObjectives.size > 0;
      case 1: return selectedNodes.size > 0;
      case 2: return rationale.trim().length > 0;
      default: return false;
    }
  }, [step, selectedObjectives, selectedNodes, rationale]);

  const steps = ['Objectives', 'OST Nodes', 'Submit'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)] flex items-center gap-2">
              <TreeDeciduous className="h-5 w-5 text-[var(--green)]" />
              Build Proposal from OST
            </h2>
            <div className="flex items-center gap-4 mt-2">
              <StepIndicator currentStep={step} totalSteps={3} />
              <span className="text-sm text-[var(--text-ter)]">
                Step {step + 1} of 3: {steps[step]}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--hover-tint)]"
          >
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 0 && (
            <ObjectiveSelector
              selectedObjectives={selectedObjectives}
              onToggle={toggleObjective}
            />
          )}
          {step === 1 && (
            <OSTNodeSelector
              selectedObjectives={selectedObjectives}
              selectedNodes={selectedNodes}
              onToggleNode={toggleNode}
            />
          )}
          {step === 2 && (
            <ReviewAndSubmit
              selectedObjectives={selectedObjectives}
              selectedNodes={selectedNodes}
              rationale={rationale}
              onRationaleChange={setRationale}
              readinessSnapshot={readinessSnapshot}
              isComputing={isComputing}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0}
            className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)] disabled:opacity-50"
          >
            Back
          </button>
          
          {step < 2 && (
            <button
              onClick={() => setStep(s => Math.min(2, s + 1))}
              disabled={!canProceed}
              className="px-4 py-2 bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BuildProposalFromOST;
